import useIsSmall from "@/hooks/useIsSmall";
import { Expense, finance, financeKeys } from "@/services/finance";
import { Alert, Badge, Button, FileInput, Group, Modal, SegmentedControl, Select, Skeleton, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { IconPaperclip } from "@tabler/icons-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import classes from "@/modules/finance/finance.module.css";
import { DateField, MoneyInput, accountOptions, faDate, faNum, isoToday, rial, useAccounts, useCategories, useFinance, useFinanceWrite } from "@/modules/finance/shared";

const STATUS: Record<Expense["status"], { label: string; color: string }> = {
  Unpaid: { label: "پرداخت‌نشده", color: "orange" },
  Paid: { label: "پرداخت‌شده", color: "green" },
  Reversed: { label: "باطل‌شده", color: "gray" },
};

export default function Expenses() {
  const { range, can } = useFinance();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<"all" | "unpaid" | "overdue">("all");
  const [selected, setSelected] = useState<Expense | null>(null);
  const [action, setAction] = useState<"pay" | "reverse" | null>(null);
  const creating = params.get("new") === "1";
  const categories = useCategories();
  const catName = (code: string) => categories.data?.find((c) => c.code === code)?.name ?? code;

  const query = useInfiniteQuery({
    queryKey: [...financeKeys.all, "expenses", range],
    queryFn: ({ pageParam }) => finance.expenses({ from: range.from, to: range.to, limit: 25, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor || undefined,
  });
  const all = query.data?.pages.flatMap((p) => p.items) ?? [];
  const first = query.data?.pages[0];
  const today = isoToday();
  const shown = all.filter((e) => filter === "all" || (e.status === "Unpaid" && (filter === "unpaid" || (e.dueDate && e.dueDate.slice(0, 10) < today))));
  const closeNew = () => { params.delete("new"); setParams(params, { replace: true }); };

  return (
    <>
      <div className={classes.kpis}>
        <div className={classes.kpi}><span>هزینه‌های ثبت‌شده</span><b>{rial(first?.totals?.amountRial)}</b><small>کل بازه، بر اساس تاریخ هزینه</small></div>
        <div className={classes.kpi}><span>تعداد</span><b>{faNum(first?.totalCount ?? 0)}</b><small>سند هزینه در بازه</small></div>
      </div>
      <section className={classes.panel}>
        <div className={classes.panelHead}>
          <div><h2>هزینه‌ها</h2><small>ابطال حذف نیست؛ سند با دلیل در سابقه می‌ماند.</small></div>
          <SegmentedControl size="xs" value={filter} onChange={(v) => setFilter(v as typeof filter)} data={[{ value: "all", label: "همه" }, { value: "unpaid", label: "پرداخت‌نشده" }, { value: "overdue", label: "سررسید گذشته" }]} />
        </div>
        {query.isLoading ? <Skeleton height={200} /> : query.isError ? <Alert color="red">دریافت هزینه‌ها انجام نشد.<Button variant="subtle" onClick={() => query.refetch()}>تلاش دوباره</Button></Alert> : !shown.length ? (
          <div className={classes.empty}>در این بازه هزینه‌ای {filter === "all" ? "ثبت نشده" : "با این وضعیت نیست"}.{can("expenses.write") && filter === "all" && <div><Button mt="sm" variant="light" color="violet" onClick={() => setParams({ new: "1" })}>ثبت هزینه</Button></div>}</div>
        ) : (
          <>
            <div className={`${classes.tableWrap} ${classes.wide}`}>
              <table>
                <thead><tr><th>عنوان</th><th>دسته</th><th>تاریخ</th><th>سررسید</th><th>وضعیت</th><th className={classes.num}>مبلغ</th><th /></tr></thead>
                <tbody>
                  {shown.map((e) => (
                    <tr key={e.id}>
                      <td><strong>{e.title}</strong>{e.note && <Text size="xs" c="dimmed" lineClamp={1}>{e.note}</Text>}</td>
                      <td>{catName(e.categoryCode)}</td>
                      <td>{faDate(e.businessDate)}</td>
                      <td>{e.status === "Unpaid" ? faDate(e.dueDate) : "—"}</td>
                      <td><Badge variant="light" color={STATUS[e.status].color}>{STATUS[e.status].label}</Badge></td>
                      <td className={classes.num}>{rial(e.amountRial)}</td>
                      <td><Actions expense={e} onPick={(a) => { setSelected(e); setAction(a); }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={classes.cards}>
              {shown.map((e) => (
                <div key={e.id} className={classes.card}>
                  <div className={classes.cardTop}><strong>{e.title}</strong><span className={classes.amount}>{rial(e.amountRial)}</span></div>
                  <div className={classes.cardMeta}><span>{catName(e.categoryCode)} · {faDate(e.businessDate)}</span><Badge variant="light" color={STATUS[e.status].color}>{STATUS[e.status].label}</Badge></div>
                  <Actions expense={e} onPick={(a) => { setSelected(e); setAction(a); }} />
                </div>
              ))}
            </div>
            {query.hasNextPage && <Button variant="default" loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>نمایش بیشتر</Button>}
          </>
        )}
      </section>

      {creating && <ExpenseForm onClose={closeNew} />}
      {selected && action === "pay" && <PayExpense expense={selected} onClose={() => setAction(null)} />}
      {selected && action === "reverse" && <ReverseExpense expense={selected} onClose={() => setAction(null)} />}
    </>
  );

  function Actions({ expense, onPick }: { expense: Expense; onPick: (a: "pay" | "reverse") => void }) {
    if (!can("expenses.write") || expense.status === "Reversed") return null;
    return (
      <Group gap={6} wrap="nowrap" justify="flex-end">
        {expense.status === "Unpaid" && <Button size="xs" variant="light" color="violet" onClick={() => onPick("pay")}>ثبت پرداخت</Button>}
        <Button size="xs" variant="subtle" color="gray" onClick={() => onPick("reverse")}>ابطال</Button>
      </Group>
    );
  }
}

function useSheet() {
  return useIsSmall() ? { fullScreen: true, radius: 0 } : { size: "lg", centered: true };
}

function ExpenseForm({ onClose }: { onClose: () => void }) {
  const sheet = useSheet();
  const accounts = useAccounts();
  const categories = useCategories();
  const [v, setV] = useState({ title: "", categoryCode: "", amount: "", date: isoToday(), paid: "paid", accountId: "", paidAt: isoToday(), reference: "", dueDate: "", note: "" });
  const [file, setFile] = useState<File | null>(null);
  // Kept once uploaded, so retrying a failed save does not upload the receipt twice.
  const [uploaded, setUploaded] = useState<{ file: File; id: number } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { if (!v.categoryCode && categories.data?.[0]) setV((s) => ({ ...s, categoryCode: categories.data![0].code })); }, [categories.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = useFinanceWrite((f: File, key) => finance.uploadAttachment(f, key));
  const save = useFinanceWrite((body: object, key) => finance.createExpense(body, key), { success: "هزینه ثبت شد", onSuccess: onClose });

  const submit = async () => {
    if (!v.title.trim()) return setError("عنوان هزینه را بنویسید.");
    if (!(Number(v.amount) > 0)) return setError("مبلغ صحیح و بیشتر از صفر وارد کنید.");
    if (v.paid === "paid" && !v.accountId) return setError("حساب پرداخت را انتخاب کنید.");
    if (v.paid === "paid" && v.paidAt < v.date) return setError("تاریخ پرداخت نباید قبل از تاریخ هزینه باشد.");
    if (file && file.size > 5 * 1024 * 1024) return setError("حجم رسید حداکثر ۵ مگابایت است.");
    setError("");
    let attachmentIds: number[] = [];
    if (file) {
      const id = uploaded?.file === file ? uploaded.id : (await upload.run(file))?.id;
      if (!id) return setError("بارگذاری رسید انجام نشد. دوباره تلاش کنید؛ فرم پاک نمی‌شود.");
      setUploaded({ file, id });
      attachmentIds = [id];
    }
    await save.run({
      title: v.title.trim(), categoryCode: v.categoryCode, amountRial: v.amount, businessDate: v.date, note: v.note.trim() || undefined, attachmentIds,
      ...(v.paid === "paid"
        ? { payment: { status: "PAID", accountId: Number(v.accountId), paidAt: `${v.paidAt}T12:00:00`, reference: v.reference.trim() || undefined } }
        : { dueDate: v.dueDate || undefined }),
    });
  };

  return (
    <Modal opened onClose={onClose} title="ثبت هزینه" {...sheet}>
      <Stack>
        <div className={classes.fields}>
          <TextInput label="عنوان هزینه" withAsterisk maxLength={80} placeholder="مثلاً خرید رنگ مو" value={v.title} onChange={(e) => setV({ ...v, title: e.currentTarget.value })} />
          <Select label="دسته" data={(categories.data ?? []).map((c) => ({ value: c.code, label: c.name }))} value={v.categoryCode} onChange={(c) => setV({ ...v, categoryCode: c ?? "" })} allowDeselect={false} />
          <MoneyInput label="مبلغ" withAsterisk value={v.amount} onChange={(amount) => setV({ ...v, amount })} />
          <DateField label="تاریخ هزینه" value={v.date} max={isoToday()} onChange={(date) => setV({ ...v, date })} />
        </div>
        <SegmentedControl value={v.paid} onChange={(paid) => setV({ ...v, paid })} data={[{ value: "paid", label: "پرداخت شده" }, { value: "due", label: "هنوز پرداخت نشده" }]} />
        {v.paid === "paid" ? (
          <div className={classes.fields}>
            <Select label="از حساب" withAsterisk placeholder="صندوق یا بانک" data={accountOptions(accounts.data)} value={v.accountId} onChange={(a) => setV({ ...v, accountId: a ?? "" })} />
            <DateField label="تاریخ پرداخت" value={v.paidAt} max={isoToday()} onChange={(paidAt) => setV({ ...v, paidAt })} />
            <TextInput label="شماره پیگیری (اختیاری)" value={v.reference} onChange={(e) => setV({ ...v, reference: e.currentTarget.value })} />
          </div>
        ) : (
          <div className={classes.fields}><DateField label="سررسید" value={v.dueDate} onChange={(dueDate) => setV({ ...v, dueDate })} /></div>
        )}
        <Textarea label="توضیح (اختیاری)" autosize minRows={2} value={v.note} onChange={(e) => setV({ ...v, note: e.currentTarget.value })} />
        <FileInput label="رسید (اختیاری)" placeholder="JPG، PNG، WebP یا PDF تا ۵ مگابایت" accept="image/jpeg,image/png,image/webp,application/pdf" leftSection={<IconPaperclip size={16} />} clearable value={file} onChange={setFile} />
        <p className={classes.note}>خرید کالای قابل فروش را از «فروش محصول ← ورود کالا» ثبت کنید، نه اینجا.</p>
        {error && <Text c="red" size="sm" role="alert">{error}</Text>}
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" loading={save.pending || upload.pending} onClick={submit}>ثبت هزینه</Button></Group>
      </Stack>
    </Modal>
  );
}

function PayExpense({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const sheet = useSheet();
  const accounts = useAccounts();
  const [accountId, setAccountId] = useState("");
  const [paidAt, setPaidAt] = useState(isoToday());
  const [reference, setReference] = useState("");
  const pay = useFinanceWrite((body: { accountId: number; paidAt: string; reference?: string }, key) => finance.payExpense(expense.id, body, key), { success: "پرداخت هزینه ثبت شد", onSuccess: onClose });
  return (
    <Modal opened onClose={onClose} title="ثبت پرداخت هزینه" {...sheet}>
      <Stack>
        <Text size="sm">{expense.title} · <b>{rial(expense.amountRial)}</b></Text>
        <Select label="از حساب" withAsterisk data={accountOptions(accounts.data)} value={accountId} onChange={(a) => setAccountId(a ?? "")} />
        <DateField label="تاریخ پرداخت" value={paidAt} max={isoToday()} onChange={setPaidAt} />
        <TextInput label="شماره پیگیری (اختیاری)" value={reference} onChange={(e) => setReference(e.currentTarget.value)} />
        <p className={classes.note}>این دکمه پرداختی را که انجام شده ثبت می‌کند؛ پولی منتقل نمی‌کند. پرداخت به اندازهٔ کل مبلغ است.</p>
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" disabled={!accountId} loading={pay.pending} onClick={() => pay.run({ accountId: Number(accountId), paidAt: `${paidAt}T12:00:00`, reference: reference.trim() || undefined })}>ثبت پرداخت انجام‌شده</Button></Group>
      </Stack>
    </Modal>
  );
}

function ReverseExpense({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const sheet = useSheet();
  const accounts = useAccounts();
  const paid = expense.status === "Paid";
  const [effect, setEffect] = useState<"accrual" | "nocash" | "cashreturn">(paid ? "nocash" : "accrual");
  const [reason, setReason] = useState("");
  const [accountId, setAccountId] = useState("");
  const reverse = useFinanceWrite((body: { effect: typeof effect; reason: string; accountId?: number | null }, key) => finance.reverseExpense(expense.id, body, key), { success: "هزینه باطل شد", onSuccess: onClose });
  const help = { accrual: "تعهد پرداخت برمی‌گردد؛ پولی جابه‌جا نشده بود.", nocash: "ثبت اشتباه بوده و پولی برنگشته؛ مانده حساب تغییر نمی‌کند.", cashreturn: "پول واقعاً به حساب برگشته؛ مانده آن حساب زیاد می‌شود." }[effect];
  return (
    <Modal opened onClose={onClose} title="ابطال هزینه" {...sheet}>
      <Stack>
        <Text size="sm">{expense.title} · <b>{rial(expense.amountRial)}</b></Text>
        <p className={classes.note}>سند حذف نمی‌شود و با دلیل در سابقه می‌ماند.</p>
        {paid && <SegmentedControl value={effect} onChange={(e) => setEffect(e as typeof effect)} data={[{ value: "nocash", label: "پول برنگشته" }, { value: "cashreturn", label: "پول برگشته" }]} />}
        <Text size="sm" c="dimmed">{help}</Text>
        {effect === "cashreturn" && <Select label="برگشت به حساب" withAsterisk data={accountOptions(accounts.data)} value={accountId} onChange={(a) => setAccountId(a ?? "")} />}
        <Textarea label="دلیل ابطال" withAsterisk minRows={2} autosize value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="red" disabled={!reason.trim() || (effect === "cashreturn" && !accountId)} loading={reverse.pending} onClick={() => reverse.run({ effect, reason: reason.trim(), accountId: effect === "cashreturn" ? Number(accountId) : null })}>تأیید ابطال</Button></Group>
      </Stack>
    </Modal>
  );
}
